import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { omit } from 'lodash';

import type { AiChatReference, Insight } from '~/components/AiChat/types';
import {
  INSIGHT_FEATURE_FLAG_MAPPING,
  type InsightsResponse,
  orderInsightsByKey,
  processInsightsResponse,
} from '~/components/AiChat/utils/insightUtils';
import { useClaim } from '~/components/ClaimContainer';
import { useClaimInsightsCount } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useClaimInsightsCount';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { isFeatureEnabled, reportAxiosError } from '~/Utils';

/** One insight key's loaded history: older versions only, newest first, appended page by page. */
export interface InsightHistoryState {
  versions: Insight[];
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
}

export interface IInsightsDataContextData {
  insights: Insight[];
  references: Record<string, AiChatReference>;
  isLoadingFirstTime: boolean;
  isLoading: boolean;
  isError: boolean;
  reloadData: () => Promise<void>;
  dismissInsight: (insight: Insight) => Promise<void>;
  /** Loaded history per insight_key. A key is absent until its history is first requested. */
  history: Record<string, InsightHistoryState>;
  /** Fetch the next page of older versions for this insight's key. Safe to call repeatedly. */
  loadMoreHistory: (insight: Insight) => Promise<void>;
  /** Re-attempt a page that failed. Paging stops on failure, so recovery has to be asked for. */
  retryHistory: (insight: Insight) => Promise<void>;
}

const EMPTY_HISTORY: InsightHistoryState = { versions: [], hasMore: true, isLoading: false, isError: false };

/** [insight_key, id] for each insight in a list read, which is already latest-per-key. */
const latestVersionIds = (insights: Insight[]): [string, string][] =>
  insights.map((insight) => [insight.insight_key, insight.id]);

const InsightsDataContext = React.createContext<IInsightsDataContextData>({
  insights: [],
  references: {},
  isLoadingFirstTime: true,
  isLoading: true,
  isError: false,
  reloadData: Promise.resolve,
  dismissInsight: () => Promise.resolve(),
  history: {},
  loadMoreHistory: () => Promise.resolve(),
  retryHistory: () => Promise.resolve(),
});

export const InsightsDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();
  const { reloadUndismissedCount } = useClaimInsightsCount();

  // READ, matching the verb the GET routes enforce server-side. Gating the tab on FULL here would
  // hide it from a profile the API happily serves.
  const hasInsightsPermission = useHasPermission({
    action: PERMISSION_ACTIONS.CLIVE_CLAIM_INSIGHTS,
    verb: PERMISSION_VERBS.READ,
  });

  const [insightsResponse, setInsightsResponse] = useState<InsightsResponse>({ insights: [], references: {} });
  // Start in the loading state only when a fetch will actually happen, so users
  // without the permission never see the insights loading block.
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(hasInsightsPermission);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [history, setHistory] = useState<Record<string, InsightHistoryState>>({});

  // Refs, not effects, hold the authoritative copies below. The consumer that drives paging is a
  // descendant, and React flushes a child's effects before its parent's — so a mirror kept in an
  // effect here is a commit stale exactly when the timeline reads it, which re-issues the previous
  // cursor and appends the same page twice. Writing through `applyHistory` keeps the ref current
  // at call time; `history` exists only so React re-renders.
  const historyRef = useRef<Record<string, InsightHistoryState>>({});
  const applyHistory = useCallback(
    (updater: (prev: Record<string, InsightHistoryState>) => Record<string, InsightHistoryState>) => {
      historyRef.current = updater(historyRef.current);
      setHistory(historyRef.current);
    },
    []
  );

  // Keys with a request in flight. The intersection sentinel fires several times per scroll, so
  // without a synchronous guard the same page would be requested — and appended — more than once.
  const keysLoadingHistory = useRef<Set<string>>(new Set());

  // The latest version id per key, as of the last list read, so a refetch can tell which keys had
  // their history anchor move. Only a list read can move an anchor — the dismiss flip and the
  // reference merge below change fields, never which version is latest — so this is written in one
  // place, synchronously, rather than mirrored from state.
  const latestIdByKeyRef = useRef<Record<string, string>>({});

  const isInsightEnabled = useCallback(
    (insight: Insight): boolean => {
      for (const [keyPattern, featureFlagName] of Object.entries(INSIGHT_FEATURE_FLAG_MAPPING)) {
        if (insight.insight_key.startsWith(keyPattern)) {
          return isFeatureEnabled(userOrganization, featureFlagName);
        }
      }
      return true;
    },
    [userOrganization]
  );

  const fetchInsights = useCallback(async () => {
    if (!hasInsightsPermission) {
      setIsLoadingFirstTime(false);
      return;
    }

    try {
      setIsLoading(true);
      const res = await axios.get(`/api/v1/claims/${claim.id}/insights`);
      const processedResponse = processInsightsResponse(res.data);

      // A key's cached history is "everything older than the latest version". If a regeneration
      // made a different version latest, that anchor moved and the cached page is no longer the
      // right slice — so discard it and let the next open refetch. Keys whose latest is unchanged
      // keep their cache, which is what stops a dismiss from visibly reloading an open timeline.
      const movedKeys = latestVersionIds(processedResponse.insights).filter(
        ([key, id]) => latestIdByKeyRef.current[key] !== undefined && latestIdByKeyRef.current[key] !== id
      );
      if (movedKeys.length > 0) {
        applyHistory((prev) =>
          omit(
            prev,
            movedKeys.map(([key]) => key)
          )
        );
      }

      latestIdByKeyRef.current = Object.fromEntries(latestVersionIds(processedResponse.insights));
      // The insights are the list read's, but the references are merged: a history page contributes
      // references for documents only an older version cites, and a wholesale replace here would
      // drop them and leave an open timeline's reference chips unresolved.
      setInsightsResponse((prev) => ({
        ...processedResponse,
        references: { ...prev.references, ...processedResponse.references },
      }));
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
      if (isLoadingFirstTime) {
        setIsLoadingFirstTime(false);
      }
    }
  }, [claim.id, hasInsightsPermission, isLoadingFirstTime, applyHistory]);

  // Fetch once on mount
  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreHistory = useCallback(
    async (insight: Insight, { retry = false }: { retry?: boolean } = {}): Promise<void> => {
      const insightKey = insight.insight_key;
      const loaded = historyRef.current[insightKey];

      if (keysLoadingHistory.current.has(insightKey) || (!retry && loaded && !loaded.hasMore)) {
        return;
      }
      keysLoadingHistory.current.add(insightKey);

      // The cursor is a version id we already hold: the latest version for the first page, then the
      // last row rendered. The endpoint returns strictly older versions, so anchoring on the id the
      // card is displaying is what keeps the card and the timeline a clean partition.
      const before = loaded?.versions.at(-1)?.id ?? insight.id;

      applyHistory((prev) => ({
        ...prev,
        [insightKey]: { ...(prev[insightKey] ?? EMPTY_HISTORY), isLoading: true, isError: false },
      }));

      try {
        const res = await axios.get(
          `/api/v1/claims/${claim.id}/insights/keys/${encodeURIComponent(insightKey)}/history`,
          {
            params: { before },
          }
        );
        const { insights: versions, references } = processInsightsResponse(res.data);

        // A history version can cite a document the latest version doesn't, so its references have
        // to reach the shared map or its reference chips render unresolved.
        setInsightsResponse((prev) => ({ ...prev, references: { ...prev.references, ...references } }));
        applyHistory((prev) => {
          // A list read that landed while this page was in flight may have moved the anchor and
          // dropped the cache. Writing the page back now would restore a slice of the wrong
          // version's history, so discard it and let the next open refetch.
          if (latestIdByKeyRef.current[insightKey] !== insight.id) {
            return prev;
          }
          const current = prev[insightKey] ?? EMPTY_HISTORY;
          return {
            ...prev,
            [insightKey]: {
              versions: [...current.versions, ...versions],
              hasMore: Boolean(res.data?.has_more),
              isLoading: false,
              isError: false,
            },
          };
        });
      } catch (error) {
        // hasMore goes false on failure so the scroll sentinel stops asking. Without it the
        // isLoading true→false transition re-fires the effect while the sentinel is still on
        // screen, and a failing endpoint gets hammered in a tight loop. Recovery is the explicit
        // retry below, not an automatic one.
        applyHistory((prev) => {
          if (latestIdByKeyRef.current[insightKey] !== insight.id) {
            return prev;
          }
          return {
            ...prev,
            [insightKey]: { ...(prev[insightKey] ?? EMPTY_HISTORY), isLoading: false, isError: true, hasMore: false },
          };
        });
        await reportAxiosError(error, false);
      } finally {
        keysLoadingHistory.current.delete(insightKey);
      }
    },
    [claim.id, applyHistory]
  );

  const retryHistory = useCallback(
    (insight: Insight): Promise<void> => loadMoreHistory(insight, { retry: true }),
    [loadMoreHistory]
  );

  const dismissInsight = useCallback(
    async (insight: Insight): Promise<void> => {
      if (insight.is_dismissed) {
        return;
      }
      try {
        await axios.post(`/api/v1/claims/${claim.id}/insights/${insight.id}/dismiss`);
        setInsightsResponse((prev) => ({
          ...prev,
          insights: prev.insights.map((item) => (item.id === insight.id ? { ...item, is_dismissed: true } : item)),
        }));
        // The timeline renders from this cache, not from the list, so a dismissed history version
        // keeps offering "Dismiss" unless the cached copy is flipped too.
        applyHistory((prev) => {
          const current = prev[insight.insight_key];
          if (!current?.versions.some((version) => version.id === insight.id)) {
            return prev;
          }
          return {
            ...prev,
            [insight.insight_key]: {
              ...current,
              versions: current.versions.map((version) =>
                version.id === insight.id ? { ...version, is_dismissed: true } : version
              ),
            },
          };
        });
        // Refetch so a version generated since the tab loaded surfaces as the latest; the dismissed
        // one then drops into history. fetchInsights discards the cached history of any key whose
        // latest actually moved, so an open timeline only reloads when it has to.
        await fetchInsights();
        // The sidebar badge counts every undismissed version across the claim, which this list read
        // cannot produce — it is latest-per-key — and it lives above this provider, so the count has
        // to be reloaded through its own context or the tab keeps showing the pre-dismiss number.
        // Both the latest card and a history node dismiss through here, so this one call covers both.
        await reloadUndismissedCount();
      } catch (error) {
        await reportAxiosError(error, false);
      }
    },
    [claim.id, fetchInsights, applyHistory, reloadUndismissedCount]
  );

  const { insights, references } = insightsResponse;
  const filteredInsights = insights?.filter(isInsightEnabled) || [];
  const orderedInsights = filteredInsights.sort(orderInsightsByKey);

  return (
    <InsightsDataContext.Provider
      value={{
        insights: orderedInsights,
        references,
        isLoadingFirstTime,
        isLoading,
        isError,
        reloadData: fetchInsights,
        dismissInsight,
        history,
        loadMoreHistory,
        retryHistory,
      }}
    >
      {children}
    </InsightsDataContext.Provider>
  );
};

export const useInsightsData = (): IInsightsDataContextData => {
  return React.useContext(InsightsDataContext);
};
